/**
 * Render isomorfo del correo de comunicaciones (cuerpo + marco).
 * Sin process.env ni I/O: lo usa Azure Functions y el admin (Vite alias).
 */

export const EMAIL_LEGAL_CONTROLLER = 'AIPECES S.L.U.';
export const DEFAULT_COMM_LOGO_URL = 'https://www.aipeces.com/assets/fishwe-logo.png';
export const DEFAULT_COMM_HEADER_COLOR = '#0c4a6e';
const DEFAULT_SIGNATURE = 'Alberto M., de AiPeces';
export const DEFAULT_COMM_FOOTER_TEXT =
  'Este mensaje lo envía AIPECES S.L.U., responsable del tratamiento, en relación con tu cuenta o actividad en la plataforma. Puedes elegir qué correos recibes desde la app: en tu perfil, pulsa el engranaje › Editar perfil › Sistema.';

export type CommEmailBrand = {
  logoUrl: string;
  headerColor: string;
  signature: string;
  footerText: string;
};

export const DEFAULT_COMM_EMAIL_BRAND: CommEmailBrand = {
  logoUrl: '',
  headerColor: DEFAULT_COMM_HEADER_COLOR,
  signature: DEFAULT_SIGNATURE,
  footerText: DEFAULT_COMM_FOOTER_TEXT,
};

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

function plainField(value: unknown, max: number): string {
  if (typeof value !== 'string') return '';
  return value.replace(/<[^>]*>/g, '').trim().slice(0, max);
}

function safeLogoUrl(value: unknown): string {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > 400 || /['"]/.test(trimmed)) return '';
  try {
    const url = new URL(trimmed);
    return url.protocol === 'https:' ? trimmed : '';
  } catch {
    return '';
  }
}

/** Campos del marco. Lo inválido vuelve al valor por defecto. */
export function parseCommEmailBrand(raw: unknown): CommEmailBrand {
  let value = raw;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value) as unknown;
    } catch {
      value = null;
    }
  }
  const obj = value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  const header = typeof obj.header_color === 'string' && HEX_COLOR.test(obj.header_color)
    ? obj.header_color
    : DEFAULT_COMM_EMAIL_BRAND.headerColor;
  return {
    logoUrl: safeLogoUrl(obj.logo_url),
    headerColor: header,
    signature: plainField(obj.signature, 120) || DEFAULT_COMM_EMAIL_BRAND.signature,
    footerText: plainField(obj.footer_text, 800) || DEFAULT_COMM_EMAIL_BRAND.footerText,
  };
}

export function escapeHtml(s: unknown): string {
  if (s === null || s === undefined) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

export function htmlToPlainText(html: string): string {
  let t = String(html || '');
  t = t.replace(/<head[\s\S]*?<\/head>/gi, ' ');
  t = t.replace(/<style[\s\S]*?<\/style>/gi, ' ');
  t = t.replace(/<script[\s\S]*?<\/script>/gi, ' ');
  t = t.replace(/<br\s*\/?>/gi, '\n');
  t = t.replace(/<\/p>/gi, '\n\n');
  t = t.replace(/<\/(div|tr|h[1-6]|li)>/gi, '\n');
  t = t.replace(/<a\s+[^>]*href=["']([^"']+)["'][^>]*>([\s\S]*?)<\/a>/gi, '$2 ($1)');
  t = t.replace(/<[^>]+>/g, ' ');
  t = t
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#039;/gi, "'");
  t = t.replace(/[ \t]{2,}/g, ' ');
  t = t
    .split('\n')
    .map((line) => line.trim())
    .join('\n');
  t = t.replace(/\n{3,}/g, '\n\n').trim();
  return t;
}

export function substituteVars(
  template: string,
  data: Record<string, string | null | undefined>
): { text: string; missing: string[] } {
  const missing: string[] = [];
  const text = template.replace(/\{([A-ZÁÉÍÓÚÑa-záéíóúñ0-9_]+)\}/g, (_m, key: string) => {
    const val = data[key];
    if (val == null || String(val).trim() === '') {
      missing.push(key);
      return '';
    }
    return String(val);
  });
  return { text, missing: [...new Set(missing)] };
}

export type ExperienceLevel = 'principiante' | 'intermedio' | 'experto';
export type WaterType = 'salada' | 'dulce' | 'ambas';

export type CommEmailUtm = {
  source?: string;
  medium?: string;
  campaign?: string;
  content?: string;
};

const DEFAULT_PUBLIC_BASE = 'https://www.aipeces.com';

/** experience_level nulo → intermedio (neutro). */
export function resolveExperienceLevel(raw: string | null | undefined): ExperienceLevel {
  const v = (raw || '').trim().toLowerCase();
  if (v === 'principiante' || v === 'experto' || v === 'intermedio') return v;
  return 'intermedio';
}

/** water_type nulo → ambas. */
export function resolveWaterType(raw: string | null | undefined): WaterType {
  const v = (raw || '').trim().toLowerCase();
  if (v === 'salada' || v === 'dulce' || v === 'ambas') return v;
  return 'ambas';
}

export function activationA0FragmentKeys(
  experienceLevel: ExperienceLevel,
  waterType: WaterType
): { peticionKey: string; contenidoKey: string; subjectKey: string } {
  return {
    peticionKey: `activacion_a0.peticion.${experienceLevel}`,
    contenidoKey: `activacion_a0.contenido.${waterType}`,
    subjectKey: `activacion_a0.subject.${experienceLevel}`,
  };
}

/** Añade el id del envío a los botones de la encuesta, para saber quién pulsó. */
export function stampSurveyLinks(body: string, deliveryId: string | null | undefined): string {
  const id = (deliveryId || '').trim();
  if (!id) return body;
  return body.replace(
    /^\[([^\]\n]+)\]\((\/por-que-te-fuiste\.html\?[^)\s]+)\)$/gm,
    (_match, label: string, url: string) => {
      if (/[?&]mid=/.test(url)) return `[${label}](${url})`;
      return `[${label}](${url}&mid=${encodeURIComponent(id)})`;
    }
  );
}

export function buildTrackedClickUrl(args: {
  clickUrl: string;
  publicBaseUrl?: string;
  deliveryId?: string | null;
  utm?: CommEmailUtm;
}): string {
  const base = (args.publicBaseUrl || DEFAULT_PUBLIC_BASE).replace(/\/$/, '');
  const path = args.clickUrl.startsWith('http')
    ? args.clickUrl
    : `${base}${args.clickUrl.startsWith('/') ? '' : '/'}${args.clickUrl}`;

  const u = new URL(path);
  u.searchParams.set('utm_source', args.utm?.source || 'email');
  u.searchParams.set('utm_medium', args.utm?.medium || 'lifecycle');
  u.searchParams.set('utm_campaign', args.utm?.campaign || 'activacion_a0');
  if (args.utm?.content) u.searchParams.set('utm_content', args.utm.content);
  if (args.deliveryId) u.searchParams.set('mid', args.deliveryId);
  return u.toString();
}

function buttonHtml(label: string, href: string): string {
  return (
    `<p style="margin:20px 0 12px;text-align:center;">` +
    `<a href="${escapeHtml(href)}" style="display:inline-block;background:#0a6ea6;color:#ffffff;` +
    `text-decoration:none;font-weight:700;padding:12px 22px;border-radius:10px;">` +
    `${escapeHtml(label)}</a></p>`
  );
}

/** Enlace de un segundo botón: https o path de la web. */
export function absoluteCommHref(url: string): string | null {
  const trimmed = url.trim();
  if (/^https:\/\//i.test(trimmed)) return trimmed;
  if (trimmed.startsWith('/') && !trimmed.startsWith('//')) {
    return `${DEFAULT_PUBLIC_BASE}${trimmed}`;
  }
  return null;
}

export function bodyToHtml(
  body: string,
  clickUrl: string | null,
  imageUrl?: string | null,
  imageAlt?: string | null
): string {
  const lines = body.split(/\n/);
  const parts: string[] = [];
  let para: string[] = [];
  const image = (imageUrl || '').trim();
  if (/^https:\/\//i.test(image)) {
    const alt = (imageAlt ?? '').trim();
    parts.push(
      `<p style="margin:0 0 16px;text-align:center;">` +
        `<img src="${escapeHtml(image)}" alt="${escapeHtml(alt)}" style="max-width:100%;height:auto;border-radius:12px;" /></p>`
    );
  }

  const flushPara = () => {
    if (para.length === 0) return;
    const text = para.map((line) => escapeHtml(line)).join('<br>');
    para = [];
    if (!text) return;
    parts.push(`<p style="margin:0 0 12px;color:#0f172a;">${text}</p>`);
  };

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) {
      flushPara();
      continue;
    }
    const own = line.match(/^\[([^\]\n]+)\]\(([^)]+)\)$/);
    if (own) {
      flushPara();
      const href = absoluteCommHref(own[2]);
      if (href) parts.push(buttonHtml(own[1].trim(), href));
      else parts.push(`<p style="margin:0 0 12px;font-weight:700;">${escapeHtml(own[1].trim())}</p>`);
      continue;
    }
    const cta = line.match(/^\[([^\]\n]+)\]$/);
    if (cta) {
      flushPara();
      const label = cta[1].trim();
      if (clickUrl) parts.push(buttonHtml(label, clickUrl));
      else parts.push(`<p style="margin:0 0 12px;font-weight:700;">${escapeHtml(label)}</p>`);
      continue;
    }
    para.push(line);
  }
  flushPara();
  return parts.join('\n');
}

export type RuleCopyParts = {
  text: string;
  primaryLabel: string;
  secondaryLabel: string;
  secondaryUrl: string;
};

/** Separa el texto de los botones `[Texto]` y `[Texto](url)`. */
export function parseRuleCopy(body: string): RuleCopyParts {
  const textLines: string[] = [];
  let primaryLabel = '';
  let secondaryLabel = '';
  let secondaryUrl = '';
  for (const raw of String(body || '').split('\n')) {
    const line = raw.trim();
    const own = line.match(/^\[([^\]\n]+)\]\(([^)]+)\)$/);
    if (own && !secondaryLabel) {
      secondaryLabel = own[1].trim();
      secondaryUrl = own[2].trim();
      continue;
    }
    const primary = line.match(/^\[([^\]\n]+)\]$/);
    if (primary && !primaryLabel) {
      primaryLabel = primary[1].trim();
      continue;
    }
    textLines.push(raw);
  }
  return {
    text: textLines.join('\n').replace(/\n{3,}/g, '\n\n').trim(),
    primaryLabel,
    secondaryLabel,
    secondaryUrl,
  };
}

export function composeRuleCopy(parts: RuleCopyParts): string {
  const chunks: string[] = [];
  const text = parts.text.trim();
  if (text) chunks.push(text);
  if (parts.primaryLabel.trim()) chunks.push(`[${parts.primaryLabel.trim()}]`);
  if (parts.secondaryLabel.trim() && parts.secondaryUrl.trim()) {
    chunks.push(`[${parts.secondaryLabel.trim()}](${parts.secondaryUrl.trim()})`);
  }
  return chunks.join('\n\n');
}

export type CommEmailDocumentInput = {
  subject: string;
  headline: string;
  bodyHtml: string;
  preheader?: string | null;
  brandEyebrow?: string;
  footerHtml?: string;
  unsubscribeUrl?: string | null;
  unsubscribeFooterStyle?: 'default' | 'activation';
  signature?: string | null;
  logoUrl?: string;
  brand?: CommEmailBrand;
  /** Atributo lang del <html>. Default es. */
  htmlLang?: string;
};

function defaultCommFooter(opts: {
  unsubscribeUrl?: string | null;
  signature: string | null;
  footerText: string;
  unsubscribeFooterStyle?: 'default' | 'activation';
}): string {
  const style = opts.unsubscribeFooterStyle ?? 'default';
  let unsub = '';
  if (opts.unsubscribeUrl) {
    const href = escapeHtml(opts.unsubscribeUrl);
    const inert = opts.unsubscribeUrl === '#' ? 'pointer-events:none;' : '';
    if (style === 'activation') {
      unsub = `<p style="margin:16px 0 0;font-size:13px;line-height:1.55;color:#475569;">Si no quieres recibir estos correos, <a href="${href}" style="color:#0a6ea6;font-weight:700;text-decoration:underline;${inert}">darte de baja</a>.</p>`;
    } else {
      unsub = `<p style="margin:12px 0 0;">Si no quieres recibir estos correos, <a href="${href}" style="color:#0a6ea6;${inert}">darte de baja</a>.</p>`;
    }
  }
  const signature = opts.signature
    ? `<p style="margin:0 0 8px;">${escapeHtml(opts.signature)}</p>`
    : '';
  const footer = escapeHtml(opts.footerText).replace(/\n/g, '<br>');
  return `
    ${signature}
    <p style="margin:0;color:#64748b;font-size:11px;line-height:1.5;">${footer}</p>
    ${unsub}
  `;
}

function brandHeaderCell(eyebrow: string, headline: string, logoUrl: string, headerColor: string): string {
  const color = HEX_COLOR.test(headerColor) ? headerColor : DEFAULT_COMM_HEADER_COLOR;
  return `
                                <td class="aipeces-header" bgcolor="${color}" data-ogsb="${color}" data-ogsc="#ffffff" style="background-color:${color};background-image:linear-gradient(135deg,${color},#111827);padding:24px 32px;color:#ffffff;">
                                    <table role="presentation" cellspacing="0" cellpadding="0">
                                        <tr>
                                            <td style="padding:0 14px 0 0;vertical-align:middle;">
                                                <img src="${escapeHtml(logoUrl)}" alt="" width="40" height="40" style="display:block;border:0;border-radius:10px;" />
                                            </td>
                                            <td style="vertical-align:middle;color:#ffffff;">
                                                <div style="font-size:14px;letter-spacing:0.08em;text-transform:uppercase;color:#e0f2fe;">${escapeHtml(eyebrow)}</div>
                                                <div style="font-size:22px;font-weight:800;margin-top:6px;color:#ffffff;">${escapeHtml(headline)}</div>
                                            </td>
                                        </tr>
                                    </table>
                                </td>
  `;
}

/** Documento HTML completo que Graph envía (mismo markup que buildEmailShell). */
export function buildCommEmailDocument(input: CommEmailDocumentInput): string {
  const brand = input.brand ?? DEFAULT_COMM_EMAIL_BRAND;
  const signature = input.signature === undefined ? brand.signature : input.signature;
  const logoUrl = brand.logoUrl || input.logoUrl || DEFAULT_COMM_LOGO_URL;
  const brandEyebrow = input.brandEyebrow ?? 'AiPeces';
  const footerHtml =
    input.footerHtml ??
    defaultCommFooter({
      unsubscribeUrl: input.unsubscribeUrl,
      signature,
      footerText: brand.footerText,
      unsubscribeFooterStyle: input.unsubscribeFooterStyle,
    });

  const preheader = (input.preheader || '').trim();
  const preheaderHtml = preheader
    ? `<div style="display:none;font-size:1px;line-height:1px;max-height:0;max-width:0;opacity:0;overflow:hidden;mso-hide:all;">${escapeHtml(preheader)}</div>`
    : '';

  const htmlLang = (input.htmlLang || 'es').trim() || 'es';

  return `
        <!DOCTYPE html>
        <html lang="${escapeHtml(htmlLang)}" style="color-scheme:light;">
        <head>
            <meta charset="utf-8" />
            <meta name="viewport" content="width=device-width, initial-scale=1" />
            <meta name="color-scheme" content="light only" />
            <meta name="supported-color-schemes" content="light" />
            <title>${escapeHtml(input.subject)}</title>
            <style>
              :root { color-scheme: light only; }
              [data-ogsc] .aipeces-page { background-color:#f8fafc !important; }
              [data-ogsc] .aipeces-card { background-color:#ffffff !important; color:#0f172a !important; }
            </style>
        </head>
        <body class="aipeces-page" data-ogsb="#f8fafc" data-ogsc="#0f172a" style="margin:0;padding:0;background:#f8fafc;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;color:#0f172a;color-scheme:light;">
            ${preheaderHtml}
            <table role="presentation" class="aipeces-page" width="100%" cellspacing="0" cellpadding="0" bgcolor="#f8fafc" style="background-color:#f8fafc;padding:24px 0;">
                <tr>
                    <td align="center" bgcolor="#f8fafc">
                        <table role="presentation" class="aipeces-card" width="600" cellspacing="0" cellpadding="0" bgcolor="#ffffff" data-ogsb="#ffffff" data-ogsc="#0f172a" style="max-width:600px;background-color:#ffffff;border-radius:16px;overflow:hidden;">
                            <tr>
${brandHeaderCell(brandEyebrow, input.headline, logoUrl, brand.headerColor)}
                            </tr>
                            <tr>
                                <td class="aipeces-card" bgcolor="#ffffff" data-ogsb="#ffffff" data-ogsc="#0f172a" style="padding:24px 32px;font-size:16px;line-height:1.55;color:#0f172a;background-color:#ffffff;">${input.bodyHtml}</td>
                            </tr>
                            <tr>
                                <td bgcolor="#f1f5f9" data-ogsb="#f1f5f9" data-ogsc="#475569" style="padding:16px 32px;background-color:#f1f5f9;color:#475569;font-size:12px;">
                                    ${footerHtml}
                                </td>
                            </tr>
                        </table>
                    </td>
                </tr>
            </table>
        </body>
        </html>
    `;
}
